/**
 * Signed Distance Functions (SDF) for build shapes.
 *
 * SDF returns:
 *   negative = inside the shape (solid)
 *   positive = outside the shape (empty)
 *   zero = on the surface
 *
 * All functions expect position relative to shape center,
 * already inverse-rotated to shape's local space.
 *
 * Reference: https://iquilezles.org/articles/distfunctions/
 */
import { BuildShape } from './buildTypes.js';
import { VOXEL_SCALE } from './constants.js';
import { MIN_PART_HALF_EXTENT } from './surfaceBand.js';
// ============== Primitive SDF Functions ==============
/**
 * Sphere SDF.
 * @param p Position relative to center
 * @param radius Sphere radius
 * @returns Signed distance (negative inside)
 */
export function sdfSphere(p, radius) {
    return sdfSphereS(p.x, p.y, p.z, radius);
}
/**
 * THE SCALAR FORMS. Every primitive below has one: same arithmetic, same order of operations, no
 * `Vec3` argument. They exist because the callers that matter run per lattice point — `drawToChunk`,
 * `rasterizePartsAtScale`, the building evaluators — and a `{x, y, z}` literal per call per part was
 * measured as the bulk of their cost (~2 µs per voxel against ~0.2 µs of arithmetic). The Vec3 forms
 * stay as thin wrappers for readability where nothing is hot.
 */
export function sdfSphereS(px, py, pz, radius) {
    const length = Math.sqrt(px * px + py * py + pz * pz);
    return length - radius;
}
/**
 * ELLIPSOID SDF about Y — a sphere with its own vertical radius.
 *
 * WHY A SHAPE AND NOT A SCALED SPHERE. The big oak's crown is an oblate dome, 16 m across and 12 m
 * tall (treeOakLarge.ts): a SPHERE of the same width overshoots its top by 2 m and a CONE is a
 * Christmas tree, which is what the coarse levels of that tree used to be. Everything else in the
 * ladder — the cluster envelope at LOD 0, the coarse stand-in, the cull radius — reads the same two
 * semi-axes, so the dome had to become a primitive rather than a fudge.
 *
 * NOT AN EXACT DISTANCE. There is no closed form for the distance to an ellipsoid; this is iq's
 * standard bound `k0 * (k0 - 1) / k1`, which is exact on the axes and slightly UNDER the true
 * distance elsewhere — always with the right sign, which is all `bestWeight > 0` and the band ramp
 * need.
 */
export function sdfEllipsoid(p, radiusXZ, radiusY) {
    return sdfEllipsoidS(p.x, p.y, p.z, radiusXZ, radiusY, radiusXZ);
}
/**
 * Three semi-axes, scalar. `Math.hypot` is gone from here on purpose: it is the overflow-safe
 * varargs form and measured 40x a `Math.sqrt` of the sum of squares — and this ran twice per lattice
 * point for every coarse oak crown.
 */
export function sdfEllipsoidS(px, py, pz, rx, ry, rz) {
    const ax = px / rx, ay = py / ry, az = pz / rz;
    const k0 = Math.sqrt(ax * ax + ay * ay + az * az);
    if (k0 === 0)
        return -Math.min(rx, ry, rz); // the centre: no gradient to divide by
    const bx = ax / rx, by = ay / ry, bz = az / rz;
    const k1 = Math.sqrt(bx * bx + by * by + bz * bz);
    return (k0 * (k0 - 1)) / k1;
}
/**
 * Box (cube/cuboid) SDF.
 * @param p Position relative to center
 * @param size Half-extents (distance from center to each face)
 * @returns Signed distance (negative inside)
 */
export function sdfBox(p, size) {
    return sdfBoxS(p.x, p.y, p.z, size.x, size.y, size.z);
}
export function sdfBoxS(px, py, pz, hx, hy, hz) {
    // Distance to each face
    const dx = Math.abs(px) - hx;
    const dy = Math.abs(py) - hy;
    const dz = Math.abs(pz) - hz;
    // Outside distance (when at least one component > 0)
    const outsideX = Math.max(dx, 0);
    const outsideY = Math.max(dy, 0);
    const outsideZ = Math.max(dz, 0);
    const outsideDist = Math.sqrt(outsideX * outsideX + outsideY * outsideY + outsideZ * outsideZ);
    // Inside distance (when all components < 0, use the closest face)
    const insideDist = Math.min(Math.max(dx, dy, dz), 0);
    return outsideDist + insideDist;
}
/**
 * Cylinder SDF (Y-axis aligned).
 * @param p Position relative to center
 * @param radius Cylinder radius
 * @param halfHeight Half the cylinder height
 * @returns Signed distance (negative inside)
 */
export function sdfCylinder(p, radius, halfHeight) {
    return sdfCylinderS(p.x, p.y, p.z, radius, halfHeight);
}
export function sdfCylinderS(px, py, pz, radius, halfHeight) {
    // Distance in XZ plane to cylinder wall
    const xzLength = Math.sqrt(px * px + pz * pz);
    const dRadial = xzLength - radius;
    // Distance to top/bottom caps
    const dVertical = Math.abs(py) - halfHeight;
    // 2D box SDF in (radial, vertical) space
    const outsideR = Math.max(dRadial, 0);
    const outsideV = Math.max(dVertical, 0);
    const outsideDist = Math.sqrt(outsideR * outsideR + outsideV * outsideV);
    const insideDist = Math.min(Math.max(dRadial, dVertical), 0);
    return outsideDist + insideDist;
}
/**
 * Triangular prism SDF (extruded along Z-axis).
 * Triangle base is in XY plane, right angle at origin.
 * @param p Position relative to corner origin
 * @param sizeX Width along X
 * @param sizeY Height along Y
 * @param sizeZ Depth/extrusion along Z (half-extent)
 * @returns Signed distance (negative inside)
 */
export function sdfCone(p, baseRadius, halfHeight, topRadius) {
    return sdfConeS(p.x, p.y, p.z, baseRadius, halfHeight, topRadius);
}
export function sdfConeS(px, py, pz, baseRadius, halfHeight, topRadius) {
    // Capped cone about Y (iq's sdCappedCone), exact outside and inside. Base radius at y = -halfHeight,
    // top radius at y = +halfHeight; topRadius 0 gives a true point.
    const qx = Math.sqrt(px * px + pz * pz);
    const qy = py;
    const k1x = topRadius, k1y = halfHeight;
    const k2x = topRadius - baseRadius, k2y = 2 * halfHeight;
    const cax = qx - Math.min(qx, qy < 0 ? baseRadius : topRadius);
    const cay = Math.abs(qy) - halfHeight;
    const k2LenSq = k2x * k2x + k2y * k2y;
    const t = k2LenSq > 0
        ? Math.max(0, Math.min(1, ((k1x - qx) * k2x + (k1y - qy) * k2y) / k2LenSq))
        : 0;
    const cbx = qx - k1x + k2x * t;
    const cby = qy - k1y + k2y * t;
    const sign = (cbx < 0 && cay < 0) ? -1 : 1;
    return sign * Math.sqrt(Math.min(cax * cax + cay * cay, cbx * cbx + cby * cby));
}
export function sdfPrism(p, sizeX, sizeY, sizeZ) {
    return sdfPrismS(p.x, p.y, p.z, sizeX, sizeY, sizeZ);
}
export function sdfPrismS(x, y, z, sizeX, sizeY, sizeZ) {
    // Shift position so the triangle's center is at origin
    const px = x + sizeX / 2;
    const py = y + sizeY / 2;
    // Check if point is inside the 2D triangle (right angle at origin)
    const inTriangle = px >= 0 && py >= 0 && (px / sizeX + py / sizeY) <= 1.0;
    // Calculate 2D distance to triangle
    let dxy;
    if (inTriangle) {
        // Inside - distance to nearest edge
        const d1 = px; // Distance to Y-axis
        const d2 = py; // Distance to X-axis
        // Distance to hypotenuse (line from (sizeX, 0) to (0, sizeY))
        const d3 = ((px / sizeX + py / sizeY - 1.0) * sizeX * sizeY) /
            Math.sqrt(sizeY * sizeY + sizeX * sizeX);
        dxy = -Math.min(d1, d2, -d3); // Negative inside
    }
    else {
        // Outside triangle
        if (px < 0 && py < 0) {
            // Distance to origin corner
            dxy = Math.sqrt(px * px + py * py);
        }
        else if (px < 0) {
            // Distance to Y-axis
            dxy = -px;
        }
        else if (py < 0) {
            // Distance to X-axis
            dxy = -py;
        }
        else {
            // Distance to hypotenuse
            const num = Math.abs((px / sizeX) + (py / sizeY) - 1);
            const denom = Math.sqrt((1 / (sizeX * sizeX)) + (1 / (sizeY * sizeY)));
            dxy = num / denom;
        }
    }
    // Z-axis extrusion (half-extent)
    const dz = Math.abs(z) - sizeZ / 2;
    // Combine 2D triangle distance with Z extrusion
    const outsideXY = Math.max(dxy, 0);
    const outsideZ = Math.max(dz, 0);
    const outsideDist = Math.sqrt(outsideXY * outsideXY + outsideZ * outsideZ);
    const insideDist = Math.min(Math.max(dxy, dz), 0);
    return outsideDist + insideDist;
}
// ============== Hollow Shape Modifier ==============
/**
 * Make a shape hollow by subtracting a smaller version.
 * @param distance Original SDF distance
 * @param thickness Wall thickness
 * @returns Hollowed SDF distance
 */
export function hollowSdf(distance, thickness) {
    return Math.abs(distance) - thickness;
}
// ============== Arc Sweep Modifier ==============
/**
 * Limit a shape to an arc sweep around the Y-axis.
 * @param p Position (used to calculate angle)
 * @param sdfValue Original SDF value
 * @param arcSweep Arc angle in radians (0 to 2π)
 * @returns Modified SDF value (positive outside arc)
 */
export function arcSweepSdf(p, sdfValue, arcSweep) {
    return arcSweepSdfS(p.x, p.z, sdfValue, arcSweep);
}
export function arcSweepSdfS(px, pz, sdfValue, arcSweep) {
    // Calculate angle in XZ plane
    const angle = Math.atan2(pz, px);
    // Normalize to 0-2π range
    const normalizedAngle = angle < 0 ? angle + Math.PI * 2 : angle;
    // If outside the arc, return positive (outside)
    if (normalizedAngle > arcSweep) {
        return Math.max(sdfValue, 1); // Force outside
    }
    return sdfValue;
}
export function prepareSdf(config) {
    const sx = config.size.x * VOXEL_SCALE;
    const sy = config.size.y * VOXEL_SCALE;
    const sz = config.size.z * VOXEL_SCALE;
    const thickness = config.thickness !== undefined && config.thickness > 0
        ? Math.max(config.thickness, MIN_PART_HALF_EXTENT) * VOXEL_SCALE
        : 0;
    const openShell = thickness > 0 && !config.closed
        && (config.shape === BuildShape.CYLINDER || config.shape === BuildShape.CUBE);
    const arcSweep = config.arcSweep !== undefined && config.arcSweep > 0 && config.arcSweep < Math.PI * 2
        ? config.arcSweep : 0;
    // THE EXTENTS MUST BE SAFE TO CULL ON: a caller skips the SDF where a point is further outside the
    // box than its reach, which is only sound where the SDF is at least the distance to the box.
    // Every primitive here is, except the PRISM: `sdfPrism` measures to the infinite hypotenuse LINE,
    // so sideways from the triangle it under-reports (by up to sizeY/hypot), and a box test would drop
    // band samples it used to emit — caught against the baseline on the gable roofs. Its X/Y extents are
    // therefore unbounded (no cull); Z is an exact extrusion and stays.
    let ex, ey, ez;
    switch (config.shape) {
        case BuildShape.SPHERE:
            ex = sx;
            ey = sy;
            ez = sz;
            break;
        case BuildShape.CYLINDER:
            ex = sx;
            ey = sy;
            ez = sx;
            break;
        case BuildShape.CONE: {
            const r = Math.max(sx, sz);
            ex = r;
            ey = sy;
            ez = r;
            break;
        }
        case BuildShape.PRISM:
            ex = Infinity;
            ey = Infinity;
            ez = sz;
            break;
        default:
            ex = sx;
            ey = sy;
            ez = sz;
    }
    return {
        shape: config.shape, sx, sy, sz,
        ellipsoid: config.shape === BuildShape.SPHERE && (sy !== sx || sz !== sx),
        thickness, openShell, arcSweep,
        ex: ex + thickness, ey: ey + thickness, ez: ez + thickness,
    };
}
/**
 * The SDF of a prepared config at a point in the shape's LOCAL space (inverse-rotated), world units.
 * Same result as `sdfFromConfig(p, config)` to the bit, with no allocation.
 */
export function sdfPrepared(px, py, pz, s) {
    let d;
    switch (s.shape) {
        case BuildShape.SPHERE:
            // Sphere uses X as radius — and its own Y and Z when they differ, which makes the same shape an
            // ellipsoid (the big oak's crown). Guarded on inequality because every round sphere in the
            // codebase — presets, spherePart, lampPart, and the checksums downstream — must stay byte-identical.
            d = s.ellipsoid ? sdfEllipsoidS(px, py, pz, s.sx, s.sy, s.sz) : sdfSphereS(px, py, pz, s.sx);
            break;
        case BuildShape.CUBE:
            d = sdfBoxS(px, py, pz, s.sx, s.sy, s.sz);
            break;
        case BuildShape.CYLINDER:
            d = sdfCylinderS(px, py, pz, s.sx, s.sy);
            break;
        case BuildShape.CONE:
            d = sdfConeS(px, py, pz, s.sx, s.sy, s.sz);
            break;
        case BuildShape.PRISM:
            // Prism: X = width, Y = height, Z = depth (sdfPrism takes full dimensions)
            d = sdfPrismS(px, py, pz, s.sx * 2, s.sy * 2, s.sz * 2);
            break;
        default:
            d = sdfBoxS(px, py, pz, s.sx, s.sy, s.sz); // Fallback to box
    }
    // The hollow modifier — floored at what the lattice can hold (see MIN_PART_HALF_EXTENT's note in
    // prepareSdf's caller history): `hollowSdf` reads a HALF-thickness, and only the shell is floored,
    // never `size`, because pinPart and coarseModel's stand-ins are sub-voxel on purpose.
    if (s.thickness > 0) {
        if (s.openShell) {
            // Open top/bottom: a 2D cross-section shell (walls only), intersected with the height range so
            // the caps are removed.
            let d2D;
            if (s.shape === BuildShape.CYLINDER) {
                d2D = Math.sqrt(px * px + pz * pz) - s.sx;
            }
            else {
                const dx = Math.abs(px) - s.sx;
                const dz = Math.abs(pz) - s.sz;
                const outsideX = Math.max(dx, 0);
                const outsideZ = Math.max(dz, 0);
                d2D = Math.sqrt(outsideX * outsideX + outsideZ * outsideZ) + Math.min(Math.max(dx, dz), 0);
            }
            const wallShell = Math.abs(d2D) - s.thickness;
            const dHeight = Math.abs(py) - s.sy;
            d = Math.max(wallShell, dHeight);
        }
        else {
            d = hollowSdf(d, s.thickness);
        }
    }
    if (s.arcSweep > 0) {
        d = arcSweepSdfS(px, pz, d, s.arcSweep);
    }
    return d;
}
/**
 * Calculate SDF for a build configuration.
 * @param p Position relative to build center, in LOCAL shape space (inverse-rotated), in WORLD units
 * @param config Build configuration (size is in VOXEL units, will be scaled)
 * @returns Signed distance (negative = inside shape)
 *
 * The convenience form: prepares and evaluates in one call. Anything per-voxel should prepare once
 * (`prepareSdf`) and call `sdfPrepared`.
 */
export function sdfFromConfig(p, config) {
    return sdfPrepared(p.x, p.y, p.z, prepareSdf(config));
}
/**
 * Convert SDF distance to voxel weight.
 * SDF: negative inside, positive outside
 * Weight: positive inside (+0.5), negative outside (-0.5), 0 at surface
 *
 * @param sdfDistance Signed distance from shape surface
 * @returns Weight value clamped to [-0.5, 0.5]
 */
export function sdfToWeight(sdfDistance) {
    // Invert: SDF negative (inside) -> weight positive (solid)
    // Scale factor determines transition sharpness (1.0 = 1 voxel unit transition)
    const weight = -sdfDistance;
    return Math.max(-0.5, Math.min(0.5, weight));
}
//# sourceMappingURL=shapes.js.map